import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Lock, User, Mail, Save, Loader2, Eye, EyeOff,
  CheckCircle, AlertCircle, Shield, Key,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../services/mockData';
import { SessionUser } from '../types';

interface AccountSettingsProps {
  user: SessionUser;
}

const PasswordStrengthBar: React.FC<{ password: string }> = ({ password }) => {
  const checks = [
    { label: 'En az 8 karakter', ok: password.length >= 8 },
    { label: 'Büyük harf', ok: /[A-Z]/.test(password) },
    { label: 'Küçük harf', ok: /[a-z]/.test(password) },
    { label: 'Rakam', ok: /\d/.test(password) },
    { label: 'Özel karakter (!@#$%)', ok: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];
  const score = checks.filter(c => c.ok).length;
  const percent = (score / checks.length) * 100;

  const color =
    score < 2 ? 'bg-red-500' :
    score < 4 ? 'bg-amber-500' :
    'bg-emerald-500';

  const label =
    score < 2 ? 'Zayıf' :
    score < 4 ? 'Orta' :
    'Güçlü';

  if (!password) return null;

  return (
    <div className="space-y-2 mt-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold text-gray-600">Şifre Gücü</p>
        <p className={`text-[11px] font-bold ${
          score < 2 ? 'text-red-600' :
          score < 4 ? 'text-amber-600' :
          'text-emerald-600'
        }`}>
          {label}
        </p>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} transition-all duration-300`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <ul className="space-y-1 mt-2">
        {checks.map((c, i) => (
          <li key={i} className="flex items-center gap-1.5 text-[11px]">
            {c.ok ? (
              <CheckCircle className="w-3 h-3 text-emerald-500" />
            ) : (
              <AlertCircle className="w-3 h-3 text-gray-300" />
            )}
            <span className={c.ok ? 'text-gray-700' : 'text-gray-400'}>{c.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const AccountSettings: React.FC<AccountSettingsProps> = ({ user }) => {
  // Şifre değiştirme
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isFormValid =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    newPassword === confirmPassword &&
    newPassword !== currentPassword;

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      toast.error('Yeni şifre en az 8 karakter olmalı');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    if (newPassword === currentPassword) {
      toast.error('Yeni şifre eskiyle aynı olamaz');
      return;
    }

    setSubmitting(true);
    try {
      // Önce mevcut şifreyle doğrula
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });
      if (signInError) {
        toast.error('Mevcut şifre yanlış');
        return;
      }

      // Şifre güncelle
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        toast.error('Şifre değiştirilemedi: ' + error.message);
        return;
      }

      toast.success('Şifre başarıyla değiştirildi!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e: any) {
      toast.error(e?.message || 'Bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-stack">
      {/* Header */}
      <div className="page-head">
        <div>
          <h1 className="page-title flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white shadow-md shadow-teal-300/40">
              <Shield className="w-5 h-5" />
            </span>
            Hesap Ayarları
          </h1>
          <p className="page-subtitle">Hesap güvenliği ve kişisel bilgiler</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Sol — Profil özeti */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="dribbble-panel p-6 text-center"
        >
          {/* Avatar */}
          <div className="w-20 h-20 mx-auto mb-4 rounded-3xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-teal-300/40">
            {user.name?.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?'}
          </div>

          <h2 className="text-lg font-bold text-gray-900">{user.name || 'Kullanıcı'}</h2>
          <p className="text-xs text-gray-500 mb-4">{user.role}</p>

          {user.clinicName && (
            <div className="px-3 py-2 rounded-xl bg-teal-50 border border-teal-100 mb-4">
              <p className="text-[10px] text-teal-700 font-bold uppercase tracking-wide mb-0.5">Klinik</p>
              <p className="text-sm font-semibold text-gray-900">{user.clinicName}</p>
            </div>
          )}

          <div className="space-y-2 text-left">
            <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 border border-gray-100">
              <Mail className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="text-xs text-gray-700 truncate">{user.email}</span>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 border border-gray-100">
              <User className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="text-xs text-gray-700">{user.role}</span>
            </div>
          </div>
        </motion.div>

        {/* Sağ — Şifre değiştirme formu */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-2 dribbble-panel p-6"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
              <Key className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900">Şifre Değiştir</h2>
              <p className="text-xs text-gray-500">Hesabınızın güvenliği için güçlü bir şifre kullanın</p>
            </div>
          </div>

          {/* Bilgi kutusu */}
          {user.authProvider !== 'supabase' && (
            <div className="mb-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 leading-relaxed">
                Şifre değiştirme yalnızca Supabase Auth ile giriş yapan hesaplarda aktiftir.
                Şu an <strong>{user.authProvider === 'env' ? 'ortam değişkeni' : 'misafir'}</strong> hesabı kullanıyorsunuz.
              </p>
            </div>
          )}

          <form onSubmit={handleChangePassword} className="space-y-4">
            {/* Mevcut şifre */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700">
                Mevcut Şifre <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Mevcut şifreniz"
                  disabled={user.authProvider !== 'supabase'}
                  autoComplete="current-password"
                  className="input pl-10 pr-10 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Yeni şifre */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700">
                Yeni Şifre <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Yeni şifre belirleyin"
                  disabled={user.authProvider !== 'supabase'}
                  autoComplete="new-password"
                  className="input pl-10 pr-10 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordStrengthBar password={newPassword} />
            </div>

            {/* Şifre tekrar */}
            <div className="space-y-1.5">
              <label className="block text-sm font-semibold text-gray-700">
                Yeni Şifre (Tekrar) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Yeni şifreyi tekrar girin"
                  disabled={user.authProvider !== 'supabase'}
                  autoComplete="new-password"
                  className={`input pl-10 pr-10 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    confirmPassword && newPassword !== confirmPassword
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20 bg-red-50/30'
                      : confirmPassword && newPassword === confirmPassword
                      ? 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500/20 bg-emerald-50/30'
                      : ''
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Şifreler eşleşmiyor
                </p>
              )}
              {confirmPassword && newPassword === confirmPassword && newPassword.length >= 8 && (
                <p className="text-xs text-emerald-600 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Şifreler eşleşiyor
                </p>
              )}
            </div>

            {/* Submit */}
            <div className="pt-4 border-t border-gray-100">
              <button
                type="submit"
                disabled={!isFormValid || submitting || user.authProvider !== 'supabase'}
                className="btn btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Güncelleniyor...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Şifreyi Güncelle
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AccountSettings;
